import { add, money, multiply, percentage, subtract, sum, zero } from '../money/money.js';
function assertRate(value, label) {
    if (!Number.isFinite(value) || value < 0 || value > 100) {
        throw new RangeError(`${label} doit être compris entre 0 et 100 (reçu : ${value})`);
    }
}
export function computeLine(line, currency) {
    if (!Number.isFinite(line.quantity)) {
        throw new RangeError(`Quantité invalide : ${line.quantity}`);
    }
    assertRate(line.discountRate ?? 0, 'La remise de ligne');
    assertRate(line.taxRate, 'Le taux de taxe');
    const gross = multiply(money(line.unitPrice, currency), line.quantity);
    const discount = percentage(gross, line.discountRate ?? 0);
    const subtotal = subtract(gross, discount);
    const tax = percentage(subtotal, line.taxRate);
    return {
        grossAmount: gross,
        discountAmount: discount,
        subtotal,
        taxAmount: tax,
        total: add(subtotal, tax),
    };
}
/**
 * Totaux du document.
 *
 * La remise globale est répartie proportionnellement sur les lignes AVANT le
 * calcul des taxes. C'est indispensable dès qu'un document mélange plusieurs
 * taux : appliquer la remise sur le total taxé fausserait la ventilation, et
 * donc la déclaration fiscale.
 */
export function computeDocument(input) {
    const { currency } = input;
    const globalDiscountRate = input.globalDiscountRate ?? 0;
    assertRate(globalDiscountRate, 'La remise globale');
    const lineSubtotals = input.lines.map((line) => {
        assertRate(line.discountRate ?? 0, 'La remise de ligne');
        assertRate(line.taxRate, 'Le taux de taxe');
        const gross = multiply(money(line.unitPrice, currency), line.quantity);
        const lineDiscount = percentage(gross, line.discountRate ?? 0);
        return { line, gross, lineDiscount, net: subtract(gross, lineDiscount) };
    });
    const netBeforeGlobal = sum(lineSubtotals.map((l) => l.net), currency);
    // Répartition proportionnelle de la remise globale, sans perte d'unité :
    // la dernière ligne absorbe l'écart d'arrondi cumulé.
    const globalDiscountTotal = percentage(netBeforeGlobal, globalDiscountRate);
    let distributed = zero(currency);
    const lines = lineSubtotals.map((entry, index) => {
        const isLast = index === lineSubtotals.length - 1;
        let share;
        if (globalDiscountTotal.amount === 0 || netBeforeGlobal.amount === 0) {
            share = zero(currency);
        }
        else if (isLast) {
            share = subtract(globalDiscountTotal, distributed);
        }
        else {
            share = multiply(globalDiscountTotal, entry.net.amount / netBeforeGlobal.amount);
            distributed = add(distributed, share);
        }
        const subtotal = subtract(entry.net, share);
        const tax = percentage(subtotal, entry.line.taxRate);
        return {
            grossAmount: entry.gross,
            discountAmount: add(entry.lineDiscount, share),
            subtotal,
            taxAmount: tax,
            total: add(subtotal, tax),
        };
    });
    const subtotalAmount = sum(lines.map((l) => l.subtotal), currency);
    const discountAmount = sum(lines.map((l) => l.discountAmount), currency);
    const taxAmount = sum(lines.map((l) => l.taxAmount), currency);
    // Ventilation par taux, figée sur le document à la validation.
    const byRate = new Map();
    lines.forEach((computed, index) => {
        const rate = input.lines[index].taxRate;
        const current = byRate.get(rate) ?? { base: zero(currency), amount: zero(currency) };
        byRate.set(rate, {
            base: add(current.base, computed.subtotal),
            amount: add(current.amount, computed.taxAmount),
        });
    });
    const taxBreakdown = [...byRate.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([rate, v]) => ({ rate, base: v.base, amount: v.amount }));
    return {
        lines,
        subtotalAmount,
        discountAmount,
        taxAmount,
        totalAmount: add(subtotalAmount, taxAmount),
        taxBreakdown,
    };
}
//# sourceMappingURL=totals.js.map